import React, { useState } from 'react';

import { Modal, Button, Title, Group, Stack, Text, Divider, Skeleton } from '@mantine/core';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useDeleteAthleteStatus from 'hooks/athleteStatus/useDeleteAthleteStatus';
import { AthleteRetrieve, AthleteStatus } from 'types';
import notificationHandler from 'utils/notifications';

interface IDeleteStatusModalProps {
  opened: boolean;
  athlete: AthleteRetrieve;
  athleteStatus: AthleteStatus | null;
  previousStatus: AthleteStatus | null;
  followingStatus: AthleteStatus | null;
  closeHandler: () => void;
}

const DeleteStatusModal = ({
  opened,
  athlete,
  athleteStatus,
  previousStatus,
  followingStatus,
  closeHandler,
}: IDeleteStatusModalProps): JSX.Element => {
  const { dateFormat } = useLocalization();
  const { successNotification, errorNotification } = notificationHandler();

  const deleteMutation = useDeleteAthleteStatus();

  const [clickedButton, setClickedButton] = useState('');

  const handleCloseAndReset = (): void => {
    setClickedButton('');
    closeHandler();
  };

  const handleSubmit = (replaceWithPrevious: boolean): void => {
    if (athleteStatus) {
      setClickedButton(replaceWithPrevious ? 'previous' : 'following');
      deleteMutation.mutate(
        { athleteStatusId: athleteStatus.athleteStatusId, replaceWithPrevious },
        {
          onSuccess: () => {
            successNotification('Athlete status successfully deleted');
            handleCloseAndReset();
          },
          onError: () => {
            errorNotification('Athlete status deletion failed');
            handleCloseAndReset();
          },
        },
      );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      size='md'
      centered
      title={<Title order={4}>Delete Status</Title>}
    >
      <Stack p='md'>
        <Stack spacing='xs'>
          <Group noWrap>
            <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
              Athlete Name
            </Text>
            <Text fz='sm' ta='left'>
              {athlete.fullName}
            </Text>
          </Group>
          <Group noWrap>
            <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
              Status
            </Text>
            {athleteStatus ? <AthleteStatusBadge status={athleteStatus.status} /> : <Skeleton h='sm' />}
          </Group>
          <Group noWrap>
            <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
              Date Range
            </Text>
            {athleteStatus ? (
              <Text fz='sm' ta='left'>
                {`${dayjs(athleteStatus.startDate).format(dateFormat)} - ${
                  athleteStatus.endDate ? dayjs(athleteStatus.endDate).format(dateFormat) : ''
                }`}
              </Text>
            ) : (
              <Skeleton h='sm' />
            )}
          </Group>
        </Stack>

        <Divider mx='-xl' />

        <Text color='dimmed' size='sm'>
          Do you want the previous or following status to cover this time range?
        </Text>
        <Group noWrap>
          <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
            Previous
          </Text>
          {previousStatus && <AthleteStatusBadge status={previousStatus.status} />}
        </Group>
        <Group noWrap>
          <Text fs='italic' c='dimmed' fz='sm' ta='right' w='100px'>
            Following
          </Text>
          {followingStatus && <AthleteStatusBadge status={followingStatus.status} />}
        </Group>
      </Stack>

      <Group position='right' mt='md'>
        <Button
          variant='light'
          size='sm'
          radius='md'
          onClick={handleCloseAndReset}
          disabled={deleteMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          data-cy='previousStatusButton'
          size='sm'
          radius='md'
          variant='light'
          color='red'
          loading={clickedButton === 'previous' && deleteMutation.isLoading}
          disabled={!previousStatus}
          onClick={() => handleSubmit(true)}
        >
          Previous
        </Button>
        <Button
          data-cy='followingStatusButton'
          size='sm'
          radius='md'
          variant='light'
          color='red'
          loading={clickedButton === 'following' && deleteMutation.isLoading}
          disabled={!followingStatus}
          onClick={() => handleSubmit(false)}
        >
          Following
        </Button>
      </Group>
    </Modal>
  );
};

export default DeleteStatusModal;
